/** Section 4.1. These strings travel on the wire. */

export const ROLES = ['management', 'reception', 'accountant', 'cadcam', 'tech'] as const;
export type Role = (typeof ROLES)[number];

export const ROLE_LABEL: Record<Role, string> = {
  management: 'Management',
  reception: 'Reception',
  accountant: 'Accounts',
  cadcam: 'CAD / CAM',
  tech: 'Technician',
};

export const PERMISSIONS = [
  'editDates',
  'markUrgent',
  'intake',
  'assign',
  'pay',
  'ship',
  'seeIdentity',
  'finalcheck',
  'admin',
  'editCase',
  'pricing',
  'redo',
  'deleteCase',
  'approveDeletion',
  'seeWorkProof',
  'takeOver',
  'seeCaseLog',
  // Archiving a case, and taking it out again. Section 4 of the request of 24 September 2026.
  'archiveCase',
  // Stopping somebody else's clock on a case, from the board. Request of 25 September 2026.
  'stopWork',
  // Saving a photograph out of the viewer. The request of 5 October 2026 (third), section 6.
  'downloadPhotos',
  // Adding, editing and deleting a doctor's or a patient's record. The request
  // of 6 October 2026 (second), items 4 and 5.
  'manageClients',
  // Changing a doctor's or a patient's name, which reaches every case of the
  // record. The request of 6 October 2026 (third), item 4.
  'renameClients',
  // Merging one doctor's record into another. The same request, item 1.
  'mergeDoctors',
] as const;
export type Permission = (typeof PERMISSIONS)[number];

export const PERMISSION_LABEL: Record<Permission, string> = {
  editDates: 'Edit start & delivery dates',
  markUrgent: 'Mark cases urgent',
  intake: 'Create job orders',
  assign: 'Assign technicians to cases',
  pay: 'Mark paid, hold & release',
  ship: 'Dispatch shipments & hold',
  seeIdentity: 'See doctor & patient names',
  finalcheck: 'Pass / fail Final Check',
  admin: 'Manage users & permissions',
  editCase: 'Edit a case after intake',
  pricing: 'Manage items and prices',
  redo: 'Bring an archived case back into production',
  deleteCase: 'Delete cases, or ask for a deletion',
  approveDeletion: 'Approve, refuse and restore deletions',
  seeWorkProof: "See other people's proof of work",
  takeOver: 'Take a case from another tray',
  seeCaseLog: "See a case's history and log",
  archiveCase: 'Archive cases, and take them out again',
  stopWork: "Stop somebody's clock on a case",
  downloadPhotos: 'Download photographs',
  manageClients: 'Add and edit doctors and patients',
  renameClients: 'Rename doctors and patients',
  mergeDoctors: 'Merge duplicate doctors',
};

export const VIEW_KEYS = [
  'overview',
  // A person's own page of widgets, after the Overview as the server lists it.
  // The lab's request of 12 October 2026.
  'dashboard',
  'line',
  'orders',
  'finalcheck',
  'reports',
  'archive',
  'activity',
  'staff',
  'users',
  'settings',
  'items',
  'intake',
  'shipping',
  'payments',
  'onhold',
  // Doctors and patients, each a screen of its own, after On Hold Cases as the
  // server lists them. The request of 6 October 2026 (second), item 4.
  'doctors',
  'patients',
  'queue',
  'perf',
  'stage',
  'job',
  'record',
  'scan',
] as const;
export type ViewKey = (typeof VIEW_KEYS)[number];

/**
 * The twenty views that carry a grant in the access matrix and therefore a
 * menu entry. The remaining four are sub-views reached from one of these, and
 * their access is derived, see `allowedView`.
 */
export const MENU_VIEW_KEYS = [
  'overview',
  'dashboard',
  'line',
  'orders',
  'finalcheck',
  'reports',
  'archive',
  'activity',
  'staff',
  'users',
  'settings',
  'items',
  'intake',
  'shipping',
  'payments',
  'onhold',
  'doctors',
  'patients',
  'queue',
  'perf',
] as const;
export type MenuViewKey = (typeof MENU_VIEW_KEYS)[number];

export const VIEW_LABEL: Record<ViewKey, string> = {
  overview: 'Overview',
  dashboard: 'Dashboard',
  line: 'Production line',
  orders: 'Orders',
  finalcheck: 'Final Check',
  reports: 'Reports & analytics',
  archive: 'Archive',
  activity: 'Activity log',
  staff: 'Staff productivity',
  users: 'Users & permissions',
  settings: 'Settings',
  items: 'Items',
  intake: 'New job order',
  shipping: 'Shipping & dispatch',
  payments: 'Payments',
  onhold: 'On Hold Cases',
  doctors: 'Doctors',
  patients: 'Patients',
  queue: 'Queue',
  perf: 'My performance',
  stage: 'Stage detail',
  job: 'Job',
  record: 'Case record',
  scan: 'Scan',
};

export function isRole(value: string): value is Role {
  return (ROLES as readonly string[]).includes(value);
}

export function isPermission(value: string): value is Permission {
  return (PERMISSIONS as readonly string[]).includes(value);
}

export function isViewKey(value: string): value is ViewKey {
  return (VIEW_KEYS as readonly string[]).includes(value);
}

export function isMenuViewKey(value: ViewKey): value is MenuViewKey {
  return (MENU_VIEW_KEYS as readonly ViewKey[]).includes(value);
}
